// Shared plumbing for the "import assets from an Excel file" flow.
//
// Both AllAssets and AssetRegistration POST the same workbook to the same
// endpoint (AllAssets adds ?updateExisting=, AssetRegistration adds
// ?template_id=), so the upload-progress bookkeeping and the all-or-nothing
// validation-failure handling live here instead of being copy-pasted into
// each page. Each page keeps its own toast wording and post-import refresh.
//
// Two phases the operator can actually see:
//   'uploading'  — real 0-100%, driven by axios `onUploadProgress`
//                  (e.loaded / e.total).
//   'processing' — the bytes are on the server but it still has to parse and
//                  validate every row before it answers, and there is no
//                  progress signal for that half. The bar goes indeterminate
//                  rather than sitting at a misleading "100% — done!".

import { useCallback, useRef, useState } from 'react';
import api from '../api/axios';

// The shared axios instance uses a 30s timeout, which is right for JSON but
// far too short here: on a slow site link the upload alone can eat it, and
// the server-side parse + validate of a few thousand rows runs *after* the
// upload finishes. Give imports their own generous ceiling.
export const IMPORT_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * True when an axios error is the backend's all-or-nothing validation
 * rejection: HTTP 422 with { code: 'IMPORT_VALIDATION_FAILED', errors: [...] }.
 * Everything else (network drop, 402 quota, 500) is a plain failure and keeps
 * the pre-existing toast behaviour.
 */
export function isImportValidationFailure(err) {
  const data = err?.response?.data;
  return (
    err?.response?.status === 422 &&
    !!data &&
    data.code === 'IMPORT_VALIDATION_FAILED' &&
    Array.isArray(data.errors)
  );
}

/**
 * Human-readable message for a NON-422 import failure. Mirrors what AllAssets
 * worked out the hard way: the quota gate answers 402 with a long prose error,
 * and a bare axios failure with no response body used to surface as a vacuous
 * "Import failed".
 */
export function importErrorMessage(err) {
  const data = err?.response?.data || {};
  return (
    data.error ||
    (err?.response?.status === 402 ? 'Asset quota exceeded for this company.' : null) ||
    (err?.response ? `HTTP ${err.response.status}` : null) ||
    err?.message ||
    'Import failed (open browser console for details)'
  );
}

/**
 * Drives one asset import at a time.
 *
 *   const { phase, percent, fileName, importing, report, clearReport, runImport } = useAssetImport();
 *   const outcome = await runImport(file, { url: '/assets/import?updateExisting=true' });
 *
 * `runImport` never throws. It resolves to one of:
 *   { status: 'ok',      data }    — HTTP 200, the page shows its usual summary
 *   { status: 'invalid', report }  — HTTP 422, NOTHING was imported; `report`
 *                                    is also stored in state for <ImportErrorReport>
 *   { status: 'error',   error }   — anything else; page shows its usual toast
 *   { status: 'busy' }             — refused, an import is already in flight
 */
export function useAssetImport() {
  const [phase, setPhase] = useState(null);      // null | 'uploading' | 'processing'
  const [percent, setPercent] = useState(0);     // 0-100, or null when unmeasurable
  const [fileName, setFileName] = useState('');
  const [report, setReport] = useState(null);    // the 422 body, or null
  // A ref as well as state: two clicks landing in the same tick would both
  // read a stale `phase` and fire two uploads. The ref flips synchronously.
  const inFlight = useRef(false);

  const clearReport = useCallback(() => setReport(null), []);

  const runImport = useCallback(async (file, { url = '/assets/import' } = {}) => {
    if (inFlight.current || !file) return { status: 'busy' };
    inFlight.current = true;
    setReport(null);
    setFileName(file.name);
    setPercent(0);
    setPhase('uploading');

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await api.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: IMPORT_TIMEOUT_MS,
        onUploadProgress: (e) => {
          // `e.total` is absent when the browser can't size the body. A null
          // percent tells <ImportProgress> to render indeterminate instead of
          // sitting at a lying 0%.
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : null;
          setPercent(pct);
          // Last byte is out the door — from here on it's the server parsing
          // and validating, which reports nothing back to us.
          if (pct === 100) setPhase('processing');
        },
      });
      return { status: 'ok', data: res.data };
    } catch (err) {
      if (isImportValidationFailure(err)) {
        setReport(err.response.data);
        return { status: 'invalid', report: err.response.data };
      }
      return { status: 'error', error: err };
    } finally {
      inFlight.current = false;
      setPhase(null);
      setPercent(0);
      setFileName('');
    }
  }, []);

  return {
    phase,
    percent,
    fileName,
    importing: phase !== null,
    report,
    clearReport,
    runImport,
  };
}
